import React from 'react';
import {Icon} from "types/Icon";
import SidebarNavGroup from "./SidebarNavGroup";
import SidebarNavLink from "./SidebarNavLink";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "../../context/AuthContext";
import {Permission} from "../../types/Permissions";
import FeatureFlag from "../../types/FeatureFlag";
import {BarChartIcon, GrowthHeartIcon, HomeIcon, LocationPinIcon, MegaphoneIcon, TextImageIcon, UserIcon} from "../Icon/ngage";
import {ChatIcon} from "@heroicons/react/outline";

// Types ----

export type BasicLink = {
  name: string
  to: string
  permissions?: Array<Permission>
  isHidden?: boolean
}

type Link = BasicLink & {
  icon: Icon
}

type LinkGroup = {
  name: string
  icon: Icon
  links: Array<BasicLink | LinkSubGroup>
  permissions?: Array<Permission>
  isHidden?: boolean
}

export type LinkSubGroup = {
  name: string
  links: Array<BasicLink>
  permissions?: Array<Permission>
  isHidden?: boolean
}

type MenuItem = BasicLink | Link | LinkGroup | LinkSubGroup;

// Helpers ----

function isGroup(item: MenuItem): item is LinkGroup {
  return (item as LinkGroup).links !== undefined;
}

function canAccessItem(item: MenuItem, permissions: Array<Permission>): boolean {
  if (item.isHidden) return false;
  if (!item.permissions) return true;

  return item.permissions.every(permission => permissions.includes(permission));
}

function filterItemsByPermissions(items: Array<Link | LinkGroup>, permissions: Array<Permission>): Array<Link | LinkGroup> {
  return items
    .map(item => {
      if (isGroup(item)) {
        item.links = item.links.filter(link => {
          if ("links" in link) {
            link.links = link.links.filter(subLink => canAccessItem(subLink, permissions));
            return link.links.length > 0;
          }
          return canAccessItem(link, permissions);
        })
      }
      return item;
    })
    .filter(item => canAccessItem(item, permissions) && (!isGroup(item) || item.links.length > 0));
}

// Component ----

function SidebarNav() {
  const {termFor} = useOperator();
  const {permissions, canUse} = useAuth();
  const canUseBuildings = canUse(FeatureFlag.IndoorMapping);
  const canUseAIChatbot = canUse(FeatureFlag.AIChatbot);

  const items: Array<Link | LinkGroup> = [
    {name: 'Dashboard', icon: HomeIcon, to: '/'},
    {
      name: 'Performance', icon: BarChartIcon, permissions: ['insights:read'], links: [
        {name: 'Mobile App', to: '/insights/app'},
        {name: 'User Behaviour', to: '/insights/visitors'},
        {name: 'User Insights', to: '/insights/users'},
        {name: 'Learning', to: '/insights/education'},
        {name: 'Campaigns', to: '/insights/marketing'},
        {name: 'Feedback', to: '/insights/feedback'},
        {name: 'Engagement', to: '/insights/engagement'},
        {name: 'Interactions', to: '/insights/hardware'},
        {name: 'Triggers', to: '/insights/triggers'},
      ]
    },
    {
      name: 'Engagement', icon: MegaphoneIcon, links: [
        {name: 'Audience', to: '/audience', permissions: ['users:read']},
        {name: 'Contacts', to: '/contacts', permissions: ['contacts:read']},
        {name: 'Segments', to: '/audience-segments', permissions: ['audience-segments:read']},
        {name: 'Offers', to: '/offers', permissions: ['offers:read']},
        {name: 'Messages', to: '/messages', permissions: ['messages:read']},
        {name: 'Triggers', to: '/triggers', permissions: ['triggers:read']},
        {name: 'App Stores', to: '/audience-app', permissions: ['apps:read']},
      ]
    },
    {
      name: 'App Content', icon: TextImageIcon, links: [
        {name: 'Site', links: [
            {name: termFor('Exhibit Types'), to: '/exhibit-types', permissions: ['exhibit-types:read']},
            {name: termFor('Collections'), to: '/collections', permissions: ['collections:read']},
            {name: termFor('Facts'), to: '/fact-templates', permissions: ['facts:read']},
            {name: termFor('Exhibits'), to: '/exhibits', permissions: ['exhibits:read']},
            {name: termFor('Trails'), to: '/trails', permissions: ['trails:read']},
            {name: `${termFor('Trail')} Ratings`, to: '/trail-ratings', permissions: ['trails:read']},
            {name: termFor('Facility Types'), to: '/facility-types', permissions: ['facility-types:read']},
            {name: termFor('Facilities'), to: '/facilities', permissions: ['facilities:read']},
          ]
        },
        {name: 'Featured', links: [
            {name: 'Greetings', to: '/greetings', permissions: ['greetings:read']},
            {name: 'Spotlights', to: '/spotlights', permissions: ['spotlights:read']},
            {name: 'News', to: '/articles', permissions: ['articles:read']},
            {name: 'Notes', to: '/notes', permissions: ['notes:read']},
            {name: 'Links', to: '/links', permissions: ['links:read']},
          ]
        },
        {name: 'Interactive', links: [
            // {name: 'Lesson Plans', to: '/lesson-plans', permissions: ['lesson-plans:read']},
            {name: termFor('Quiz Types'), to: '/quiz-types', permissions: ['quiz-types:read']},
            {name: termFor('Quizzes'), to: '/quizzes', permissions: ['quizzes:read']},
            {name: termFor('Achievements'), to: '/achievements', permissions: ['achievements:read']},
            {name: 'Photo Frames', to: '/frames', permissions: ['frames:read']},
          ]
        },
        {name: termFor('Events'), links: [
            {name: termFor('Event Types'), to: '/event-types', permissions: ['event-types:read']},
            {name: termFor('Calendar'), to: '/events', permissions: ['events:read']},
          ]
        },
      ]
    },
    {
      name: 'Commercial', icon: GrowthHeartIcon, links: [
        {name: termFor('Tickets'), to: '/tickets', permissions: ['tickets:read']},
        {name: termFor('Ticket Types'), to: '/ticket-types', permissions: ['ticket-types:read']},
        {name: termFor('Memberships'), to: '/memberships', permissions: ['memberships:read']},
        {name: termFor('Membership Plans'), to: '/membership-plans', permissions: ['membership-plans:read']},
        {name: termFor('Sponsorships'), to: '/sponsorships', permissions: ['sponsorships:read']},
        {name: `${termFor('Sponsorship')} Tiers`, to: '/sponsorship-tiers', permissions: ['sponsorship-tiers:read']},
      ]
    },
    {
      name: 'Operations', icon: LocationPinIcon, links: [
        // Site configuration
        {name: `${termFor('Site')} Map`, to: '/site-map', permissions: ['sites:read']},
        {name: termFor('Sites'), to: '/sites', permissions: ['sites:read']},
        {name: termFor('Zones'), to: '/zones', permissions: ['zones:read']},
        {name: 'Buildings', to: '/buildings', permissions: ['buildings:read'], isHidden: !canUseBuildings},

        // Location technology
        {name: 'Interaction Areas', to: '/interaction-areas', permissions: ['qr-codes:read']},
        {name: 'Beacons', to: '/beacons', permissions: ['beacons:read']},
        {name: 'QR Codes', to: '/qr-codes', permissions: ['qr-codes:read']},
      ]
    },
    {
      name: 'Admin', icon: UserIcon, links: [
        {name: 'Documents', to: '/documents', permissions: ['documents:read']},
        {name: 'Users', to: '/users', permissions: ['users:read']},
        {name: 'Organisations', to: '/organisations', permissions: ['organisations:read']},
        {name: 'Settings', to: '/settings', permissions: ['settings:read', 'settings:write']},
        {name: 'Support', to: '/support'},
      ],
    },
    {name: 'Conversations', icon: ChatIcon, to: '/conversations', isHidden: !canUseAIChatbot},
  ];

  const authorisedItems = filterItemsByPermissions(items, permissions);

  return (
    <nav className="px-2 space-y-1 md:flex-1">
      {authorisedItems.map(item => isGroup(item) ? (
        <SidebarNavGroup
          name={item.name}
          icon={item.icon}
          links={item.links}
          key={item.name}
        />
      ) : (
        <SidebarNavLink
          name={item.name}
          icon={item.icon}
          to={item.to}
          key={item.name}
        />
      ))}
    </nav>
  );
}

// Export ----

export default SidebarNav;